/*
 * Copyright (c) 2021. American Express Company
 */

// third-party
import $ from 'jquery';
import React, { Component, ReactComponentElement } from 'react';
import Dialog from '@mui/material/Dialog';
import ReactCrop, { Crop } from 'react-image-crop';

// first-party
import { ImageUploadViewModel } from 'src/types/view-models/image-upload.view.model';
import { ButtonPrimary, ButtonSecondary, IconAccount } from '@americanexpress/dls-react';

// styles
import 'react-image-crop/dist/ReactCrop.css';
import styles from './ImageUpload.module.scss';

export default class ImageUpload extends Component<ImageUploadViewModel> {
    public fileUrl: string = null as string;
    public reducedFileUrl: string = null as string;
    public blob: Blob = null as Blob;
    public imageRef: HTMLImageElement = null as HTMLImageElement;

    public override state = {
        src: null as string,
        isEditing: false as boolean,
        crop: {} as Crop,
        croppedImageUrl: null as string
    };

    constructor(props: ImageUploadViewModel) {
        super(props);
        this.state = {
            ...this.state,
            crop: {
                unit: '%',
                width: 100,
                aspect: this.props.aspect || 1
            } as Crop
        };
    }

    public override render(): ReactComponentElement<any> {
        /*         const addPhotoLabel: JSX.Element = this.props.addPhotoLabel ||
         <Label>
         Add photo
         </Label>; */

        return (
            <div
                className={this.props.className || styles.imageUploadContainer}
                id={this.props.id}
                onClick={this.props.onClick}
            >
                {!this.props.customRender && <div>{/* {addPhotoLabel} */}</div>}
                {!this.props.customRender && (
                    <div className={styles.uploadArea}>
                        <div>
                            <ButtonSecondary onClick={() => $('#imageUpload').click()}>Select image</ButtonSecondary>
                            <input
                                id="imageUpload"
                                type="file"
                                accept="image/*;capture=camera"
                                className={styles.imageUpload}
                                onChange={(e: any) => {
                                    this.onSelectFile(e);
                                    $('#imageUpload').val('');
                                }}
                            />
                            {this.props.subLabel}
                        </div>
                        <div className={styles.filler} />
                        {this.props.imagePreview ? (
                            <div
                                className={[styles.imagePreview, this.props.roundedPreview && styles.roundedImage].join(
                                    ' '
                                )}
                            >
                                <img alt="Preview" style={{ maxWidth: '100%' }} src={this.props.imagePreview} />
                            </div>
                        ) : null}
                        {!this.props.imagePreview && (
                            <div className="genericPerson">
                                <IconAccount />
                            </div>
                        )}
                    </div>
                )}
                {this.props.customRender && (
                    <React.Fragment>
                        {this.props.children}
                        <input
                            id={this.props.imageUploadId || 'imageUpload'}
                            type="file"
                            accept="image/*;capture=camera"
                            className={styles.imageUpload}
                            onChange={(e: any) => {
                                this.onSelectFile(e);
                                $(`${styles.imageUpload}`).val('');
                            }}
                        />
                    </React.Fragment>
                )}
                {this.state.src && (
                    <Dialog
                        open={Boolean(this.state.src)}
                        PaperProps={{ className: 'image-crop-paper' }}
                        className="image-crop-dialog"
                        key="image-crop-dialog"
                    >
                        <div className={styles.imageArea}>
                            <div>
                                <ReactCrop
                                    src={this.state.src}
                                    crop={this.state.crop}
                                    ruleOfThirds
                                    onImageLoaded={this.onImageLoaded}
                                    onComplete={this.onCropComplete}
                                    onChange={this.onCropChange}
                                />
                            </div>
                        </div>
                        <div className={styles.bottomRow}>
                            <div className={styles.buttonArea}>
                                <ButtonPrimary
                                    className={styles.addButton}
                                    onClick={() => {
                                        if (this.props.onSubmit) void this.props.onSubmit(this.blob, this.fileUrl);
                                        this.setState({ isEditing: false, src: null });
                                    }}
                                >
                                    {this.props.submitButtonName || 'Add image'}
                                </ButtonPrimary>
                                <ButtonSecondary
                                    className={styles.backButton}
                                    onClick={() => {
                                        $('#imageUpload').val('');
                                        this.setState({ isEditing: false, src: null, croppedImageUrl: null });
                                        if (this.props.onCancel) this.props.onCancel();
                                    }}
                                >
                                    {this.props.cancelButtonName || 'Cancel'}
                                </ButtonSecondary>
                            </div>
                            {this.state.croppedImageUrl && (
                                <div
                                    className={[
                                        styles.imagePreview,
                                        this.props.roundedPreview && styles.roundedImage
                                    ].join(' ')}
                                >
                                    <img alt="Crop" style={{ maxWidth: '100%' }} src={this.state.croppedImageUrl} />
                                </div>
                            )}
                        </div>
                    </Dialog>
                )}
            </div>
        );
    }

    private readonly onSelectFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (e.target.files && e.target.files.length > 0) {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                this.setState({ src: reader.result });
            });
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    private readonly onImageLoaded = (image: HTMLImageElement): void => {
        this.imageRef = image;
        this.setState({ isEditing: true });
        if (this.props.onImageLoaded) this.props.onImageLoaded(image);
    };

    private readonly onCropComplete = (crop: Crop): void => {
        this.makeClientCrop(crop).catch(console.error);
    };

    private readonly onCropChange = (crop: Crop): void => {
        this.setState({ crop });
    };

    private async makeClientCrop(crop: Crop): Promise<void> {
        if (this.imageRef && crop.width && crop.height) {
            const croppedImageUrl: string = await this.getCroppedImg(this.imageRef, crop, 'newFile.jpeg');

            this.setState({ croppedImageUrl });
        }
    }

    private async getCroppedImg(image: HTMLImageElement, crop: Crop, fileName: string): Promise<string> {
        const canvas = document.createElement('canvas');
        const pixelRatio = window.devicePixelRatio;
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const ctx = canvas.getContext('2d');

        canvas.width = crop.width * pixelRatio * scaleX;
        canvas.height = crop.height * pixelRatio * scaleY;

        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width * scaleX,
            crop.height * scaleY
        );

        return await new Promise((resolve, reject) => {
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = this.props.maxWidth || 200;
            tmpCanvas.height = this.props.maxHeight || 200;
            const tmpCtx = tmpCanvas.getContext('2d');
            tmpCtx.drawImage(canvas, 0, 0, tmpCanvas.width, tmpCanvas.height);

            tmpCanvas.toBlob(
                (blob: Blob) => {
                    this.blob = blob;
                    window.URL.revokeObjectURL(this.reducedFileUrl);
                    this.reducedFileUrl = window.URL.createObjectURL(blob);
                },
                'image/jpeg',
                1
            );

            // force dimensions for blob
            canvas.toBlob(
                (blob: any) => {
                    if (!blob) {
                        const errorMsg: string = 'Canvas is empty';
                        console.error(errorMsg);
                        reject(errorMsg);
                        return;
                    }
                    blob.name = fileName;
                    window.URL.revokeObjectURL(this.fileUrl);
                    this.fileUrl = window.URL.createObjectURL(blob);
                    resolve(this.fileUrl);
                },
                'image/jpeg',
                1
            );
        });
    }
}
